import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { DAGObject, ArticleContent, ClipboardEntry } from '@amorphous/core';
import { PillChoice, PillMode } from './components/PillChoice';
import { Header } from './components/Header';
import { Feed } from './components/Feed';
import { Composer } from './components/Composer';
import { PeerStatus } from './components/PeerStatus';
import { ProfileSetup } from './components/ProfileSetup';
import { ProfileEditor } from './components/ProfileEditor';
import { ProfileModal } from './components/ProfileModal';
import { FriendsModal } from './components/FriendsModal';
import { PermissionModal } from './components/PermissionModal';
import { ModuleHost } from './components/ModuleHost';
import { ModuleInstaller } from './components/ModuleInstaller';
import { ClipboardPanel } from './components/ClipboardPanel';
import { AccountSettings } from './components/AccountSettings';
import { ChatPanel } from './components/ChatPanel';
import { CallPanel } from './components/CallPanel';
import { UserDirectory } from './components/UserDirectory';
import type { UserInfo } from './components/UserDirectory';
import { useIdentity } from './hooks/useIdentity';
import { useDAG } from './hooks/useDAG';
import { useSettings } from './hooks/useSettings';
import { usePeers } from './hooks/usePeers';
import { useProfile } from './hooks/useProfile';
import { useKarma } from './hooks/useKarma';
import { useRelationships } from './hooks/useRelationships';
import { useModules } from './hooks/useModules';
import { useClipboard } from './hooks/useClipboard';
import { useNotifications } from './hooks/useNotifications';
import { usePresence } from './hooks/usePresence';
import { useMessages } from './hooks/useMessages';
import { useCalls } from './hooks/useCalls';
import './App.css';

declare const __BUILD_TIME__: string;
declare const __BUILD_VERSION__: string;

function App() {
  // All hooks must be called unconditionally at the top
  const { identity, loading: identityLoading, createIdentity, importIdentity } = useIdentity();
  const { articles, addObject, getVoteCounts, refresh, store, cleanup, loading: feedLoading, totalObjects, deleteObject } = useDAG();

  // Expose cleanup function globally for console access
  useEffect(() => {
    (window as unknown as { aosCleanup: typeof cleanup }).aosCleanup = cleanup;
    console.log('[AOS] Cleanup function available: await window.aosCleanup()');
  }, [cleanup]);
  const { settings, loading: settingsLoading, setPillMode, toggleTheme } = useSettings();
  const [showComposer, setShowComposer] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  const [showModuleHost, setShowModuleHost] = useState(false);
  const [showModuleInstaller, setShowModuleInstaller] = useState(false);
  const [showClipboard, setShowClipboard] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showUserDirectory, setShowUserDirectory] = useState(false);
  const [viewingProfilePubkey, setViewingProfilePubkey] = useState<string | null>(null);
  const [focusedArticleHash, setFocusedArticleHash] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [forceReady, setForceReady] = useState(false);
  const [identityAttempted, setIdentityAttempted] = useState(false);
  const [replyTo, setReplyTo] = useState<{ hash: string; title: string; author: string } | null>(null);

  // Video/audio calls - declare early so we can reference it in usePeers
  // We'll set up the actual hook below after defining the sendCallSignal callback
  const callsRef = React.useRef<ReturnType<typeof useCalls> | null>(null);

  // P2P peer management (must be called before any returns)
  const { peers, connectedCount, connect, disconnect, announce, broadcastObject, isReady, fetchContent, sendCallSignal } = usePeers(
    identity?.publicKey ?? '',
    store,
    {
      onObjectReceived: async (hashes) => {
        console.log(`[P2P] Received ${hashes.length} new objects, refreshing feed...`);
        refresh();
        // Process call objects
        if (store && callsRef.current) {
          for (const hash of hashes) {
            const obj = await store.get(hash);
            if (obj && (obj.type === 'call.offer' || obj.type === 'call.answer' || obj.type === 'call.end')) {
              callsRef.current.handleCallObject(obj);
            }
          }
        }
      },
      onCallSignal: (fromPeer, signal) => {
        // Forward call signals to the useCalls hook
        if (callsRef.current) {
          callsRef.current.handleCallSignal(fromPeer, signal);
        }
      },
    }
  );

  // Profile management
  const {
    profile,
    hasProfile,
    loading: profileLoading,
    saveProfile,
    getDisplayName,
    getProfileByPubkey,
  } = useProfile(identity, store, broadcastObject);

  // Karma tracking
  const { myKarma, getUserStats, getKarma } = useKarma(store, identity?.publicKey ?? null);

  // Relationship management (friends/blocks)
  const {
    friends,
    blocked,
    isFriend,
    isBlocked,
    toggleFriend,
    toggleBlock,
    removeFriend,
    unblockUser,
  } = useRelationships(identity, store, broadcastObject);

  // Online presence tracking
  const { isOnline, onlineFriendsCount } = usePresence({
    friends,
    peers,
  });

  // Module management
  const {
    installedModules,
    runningModules,
    pendingModule,
    loadModule,
    runModule,
    stopModule,
    uninstallModule,
    approvePending,
    denyPending,
  } = useModules(store, identity);

  // Clipboard management
  const clipboard = useClipboard({
    dagStore: store,
    identity,
    fetchContent, // For on-demand chunk fetching from peers
    onNewEntry: (_entry: ClipboardEntry) => {
      console.log('[Clipboard] New entry synced:', _entry.hash);
    },
    onSystemPaste: (_entry: ClipboardEntry, type: string) => {
      console.log('[Clipboard] System type pasted:', type, _entry.hash);
      // TODO: Handle boot images, packages, invites
    },
  });

  // Notifications management
  const {
    notifications,
    unreadCount: unreadNotificationCount,
    markRead: markNotificationRead,
    markAllRead: markAllNotificationsRead,
    remove: removeNotification,
    clearAll: clearAllNotifications,
  } = useNotifications({
    store,
    identity,
    getDisplayName,
  });

  // Direct messages
  const {
    conversations,
    getConversation,
    sendMessage,
    markConversationRead,
  } = useMessages({
    store,
    identity,
    broadcastObject,
  });

  // Video/audio calls
  const calls = useCalls({
    identity: identity ? { keyPair: identity.getKeyPair() } : null,
    dagStore: store ? {
      put: (obj: DAGObject) => store.put(obj),
      get: (hash: string) => store.get(hash),
      getAll: () => store.getAll(),
    } : null,
    sendCallSignal,
    broadcastObject,
  });

  // Keep callsRef updated so usePeers callbacks can access it
  useEffect(() => {
    callsRef.current = calls;
  }, [calls]);

  // Calculate unread messages count
  const unreadMessagesCount = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  // Build user directory data
  const allKnownUsers: UserInfo[] = useMemo(() => {
    if (!store) return [];
    const userMap = new Map<string, UserInfo>();

    // Add friends
    friends.forEach(pubkey => {
      const profile = getProfileByPubkey(pubkey);
      userMap.set(pubkey, {
        pubkey,
        displayName: getDisplayName(pubkey),
        bio: profile?.content?.bio,
        avatarUrl: profile?.content?.avatarDataUrl,
        karma: getKarma(pubkey),
        isFriend: true,
        isBlocked: false,
        isOnline: isOnline(pubkey),
      });
    });

    // Add people from conversations
    conversations.forEach(conv => {
      if (!userMap.has(conv.peerPubkey)) {
        const profile = getProfileByPubkey(conv.peerPubkey);
        userMap.set(conv.peerPubkey, {
          pubkey: conv.peerPubkey,
          displayName: getDisplayName(conv.peerPubkey),
          bio: profile?.content?.bio,
          avatarUrl: profile?.content?.avatarDataUrl,
          karma: getKarma(conv.peerPubkey),
          isFriend: friends.has(conv.peerPubkey),
          isBlocked: blocked.has(conv.peerPubkey),
          isOnline: isOnline(conv.peerPubkey),
        });
      }
    });

    // Add article authors
    articles.forEach(article => {
      if (!userMap.has(article.creator)) {
        const profile = getProfileByPubkey(article.creator);
        userMap.set(article.creator, {
          pubkey: article.creator,
          displayName: getDisplayName(article.creator),
          bio: profile?.content?.bio,
          avatarUrl: profile?.content?.avatarDataUrl,
          karma: getKarma(article.creator),
          isFriend: friends.has(article.creator),
          isBlocked: blocked.has(article.creator),
          isOnline: isOnline(article.creator),
        });
      }
    });

    return Array.from(userMap.values());
  }, [friends, blocked, conversations, articles, getDisplayName, getProfileByPubkey, getKarma, isOnline, store]);

  // Initialize identity if needed
  useEffect(() => {
    async function init() {
      if (!identityLoading && !identity) {
        await createIdentity();
      }
      setInitialized(true);
    }
    init();
  }, [identityLoading, identity, createIdentity]);

  // Safety timeout - force past loading screen after 5 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!forceReady) {
        console.warn('[AOS] Initialization timeout - forcing past loading screen');
        setForceReady(true);
        setInitialized(true);
      }
    }, 5000);
    return () => clearTimeout(timeout);
  }, [forceReady]);

  // Force create identity if stuck on "Setting up identity" screen
  useEffect(() => {
    if (forceReady && !identity && settings.hasChosenPill && !identityAttempted) {
      console.warn('[AOS] No identity after forceReady - creating now');
      setIdentityAttempted(true);
      createIdentity().catch(err => {
        console.error('[AOS] Failed to create identity:', err);
      });
    }
  }, [forceReady, identity, settings.hasChosenPill, createIdentity, identityAttempted]);

  // Refresh feed periodically as fallback (main refresh happens via onObjectReceived)
  useEffect(() => {
    if (connectedCount > 0) {
      // 30s fallback refresh - primary refresh is event-driven via onObjectReceived
      const interval = setInterval(refresh, 30000);
      return () => clearInterval(interval);
    }
  }, [connectedCount, refresh]);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', settings.theme || 'dark');
  }, [settings.theme]);

  // Deep linking: parse URL query param on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hash = params.get('hash');
    if (hash) {
      setFocusedArticleHash(hash);
    }

    // Handle browser back button
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const hash = params.get('hash');
      setFocusedArticleHash(hash);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Callback handlers (must be before any returns)
  const handleAuthorClick = useCallback((pubkey: string) => {
    setViewingProfilePubkey(pubkey);
  }, []);

  const handleShare = useCallback((hash: string) => {
    const url = `${window.location.origin}${window.location.pathname}?hash=${hash}`;
    navigator.clipboard.writeText(url).then(() => {
      console.log('Link copied to clipboard:', url);
    });
  }, []);

  const handleBackToFeed = useCallback(() => {
    setFocusedArticleHash(null);
    window.history.pushState({}, '', window.location.pathname);
  }, []);

  const getArticlesByUser = useCallback(async (pubkey: string) => {
    if (!store) return [];
    const all = await store.getByCreator(pubkey);
    return all
      .filter(obj => obj.type === 'article')
      .sort((a, b) => b.timestamp - a.timestamp) as DAGObject<ArticleContent>[];
  }, [store]);

  const getParentTitle = useCallback((hash: string): string | undefined => {
    const parent = articles.find(a => a.hash === hash);
    return parent?.content?.title;
  }, [articles]);

  const handleReply = useCallback((article: DAGObject<ArticleContent>) => {
    setReplyTo({
      hash: article.hash,
      title: article.content?.title || 'Untitled',
      author: getDisplayName(article.creator),
    });
    setShowComposer(true);
  }, [getDisplayName]);

  const handleDelete = useCallback(async (hash: string) => {
    if (!identity) return;
    try {
      await deleteObject(hash, identity);
      console.log('[App] Deleted article:', hash);
    } catch (err) {
      console.error('[App] Failed to delete:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete');
    }
  }, [identity, deleteObject]);

  // Show loading state (but don't block forever - forceReady bypasses all)
  const stillLoading = !forceReady && (identityLoading || settingsLoading || !initialized);
  if (stillLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner" />
        <p>Initializing Amorphous...</p>
        <p className="version-info">v{__BUILD_VERSION__}</p>
      </div>
    );
  }

  // Show pill choice if not yet chosen
  if (!settings.hasChosenPill) {
    return (
      <PillChoice
        onChoice={async (pill) => {
          await setPillMode(pill);
        }}
      />
    );
  }

  // Show profile setup if no profile exists (after pill choice)
  // Wait for profile loading to complete before showing setup
  if (!hasProfile && identity && !profileLoading) {
    return (
      <ProfileSetup
        defaultDisplayName={identity.shortId}
        onComplete={async (profileData) => {
          try {
            await saveProfile(profileData);
          } catch (err) {
            console.error('[ProfileSetup] Failed to save profile:', err);
            alert(`Failed to save profile: ${err instanceof Error ? err.message : 'Unknown error'}`);
          }
        }}
        onSkip={async () => {
          try {
            await saveProfile({ displayName: identity.shortId });
          } catch (err) {
            console.error('[ProfileSetup] Failed to save profile (skip):', err);
            alert(`Failed to save profile: ${err instanceof Error ? err.message : 'Unknown error'}`);
          }
        }}
      />
    );
  }

  // Main app with chosen mode
  const mode = settings.pillMode as PillMode;

  const handleModeToggle = async () => {
    const newMode = mode === 'red' ? 'blue' : 'red';
    await setPillMode(newMode);
  };

  if (!identity) {
    return (
      <div className="app-loading">
        <div className="loading-spinner" />
        <p>Setting up identity...</p>
        <p className="version-info">v{__BUILD_VERSION__}</p>
        <p className="version-info">
          force:{forceReady ? 'Y' : 'N'} load:{identityLoading ? 'Y' : 'N'} pill:{settings.hasChosenPill ? 'Y' : 'N'} tried:{identityAttempted ? 'Y' : 'N'}
        </p>
      </div>
    );
  }

  return (
    <div className={`app app-${mode}`}>
      <Header
        mode={mode}
        onModeToggle={handleModeToggle}
        onNewArticle={() => setShowComposer(true)}
        identityShortId={identity.shortId}
        theme={settings.theme}
        onThemeToggle={toggleTheme}
        profile={profile?.content}
        onProfileEdit={() => setShowProfileEditor(true)}
        karma={myKarma}
        onFriendsClick={() => setShowFriendsModal(true)}
        friendsCount={friends.size}
        onModulesClick={() => setShowModuleHost(true)}
        installedModulesCount={installedModules.length}
        runningModulesCount={runningModules.length}
        onClipboardClick={() => setShowClipboard(true)}
        clipboardCount={clipboard.entries.length}
        onAccountSettings={() => setShowAccountSettings(true)}
        notifications={notifications}
        unreadNotificationCount={unreadNotificationCount}
        onMarkNotificationRead={markNotificationRead}
        onMarkAllNotificationsRead={markAllNotificationsRead}
        onDeleteNotification={removeNotification}
        onClearAllNotifications={clearAllNotifications}
        onMessagesClick={() => setShowChat(true)}
        unreadMessagesCount={unreadMessagesCount}
        onUserDirectoryClick={() => setShowUserDirectory(true)}
      />

      <main className="app-main">
        <PeerStatus
          peers={peers}
          connectedCount={connectedCount}
          onConnect={connect}
          onDisconnect={disconnect}
          onAnnounce={announce}
          redPillMode={mode === 'red'}
          isReady={isReady}
        />

        {showComposer && (
          <Composer
            identity={identity}
            addObject={addObject}
            broadcastObject={broadcastObject}
            replyTo={replyTo ?? undefined}
            onPublish={async () => {
              await refresh();
              setShowComposer(false);
              setReplyTo(null);
            }}
            onCancel={() => {
              setShowComposer(false);
              setReplyTo(null);
            }}
          />
        )}

        {focusedArticleHash ? (
          <div className="focused-article-view">
            <button className="back-to-feed-btn" onClick={handleBackToFeed}>
              ← Back to Feed
            </button>
            {(() => {
              const article = articles.find(a => a.hash === focusedArticleHash);
              if (!article) {
                return (
                  <div className="article-not-found">
                    <p>Article not found</p>
                    <p className="mono">{focusedArticleHash.slice(0, 16)}...</p>
                  </div>
                );
              }
              return (
                <Feed
                  articles={[article]}
                  mode={mode}
                  identity={identity}
                  onAddObject={addObject}
                  getVoteCounts={getVoteCounts}
                  getDisplayName={getDisplayName}
                  onAuthorClick={handleAuthorClick}
                  onShare={handleShare}
                  blockedUsers={blocked}
                  friends={friends}
                  onReply={handleReply}
                  getParentTitle={getParentTitle}
                  loading={feedLoading}
                  onDelete={handleDelete}
                />
              );
            })()}
          </div>
        ) : (
          <Feed
            articles={articles}
            mode={mode}
            identity={identity}
            onAddObject={addObject}
            getVoteCounts={getVoteCounts}
            getDisplayName={getDisplayName}
            onAuthorClick={handleAuthorClick}
            onShare={handleShare}
            blockedUsers={blocked}
            friends={friends}
            onReply={handleReply}
            getParentTitle={getParentTitle}
            loading={feedLoading}
            peerCount={connectedCount}
            totalObjects={totalObjects}
            onDelete={handleDelete}
          />
        )}
      </main>

      {/* Profile Editor Modal */}
      {showProfileEditor && (
        <ProfileEditor
          currentProfile={profile?.content}
          onSave={async (profileData) => {
            await saveProfile(profileData);
            setShowProfileEditor(false);
          }}
          onCancel={() => setShowProfileEditor(false)}
        />
      )}

      {/* Profile View Modal */}
      {viewingProfilePubkey && (
        <ProfileModal
          pubkey={viewingProfilePubkey}
          profile={getProfileByPubkey(viewingProfilePubkey)}
          onClose={() => setViewingProfilePubkey(null)}
          getUserStats={getUserStats}
          getArticlesByUser={getArticlesByUser}
          isOwnProfile={viewingProfilePubkey === identity.publicKey}
          isFriend={isFriend(viewingProfilePubkey)}
          isBlocked={isBlocked(viewingProfilePubkey)}
          onFriend={toggleFriend}
          onBlock={toggleBlock}
        />
      )}

      {/* Friends/Blocked Modal */}
      {showFriendsModal && (
        <FriendsModal
          friends={friends}
          blocked={blocked}
          onClose={() => setShowFriendsModal(false)}
          onRemoveFriend={removeFriend}
          onUnblock={unblockUser}
          onViewProfile={handleAuthorClick}
          getDisplayName={getDisplayName}
          isOnline={isOnline}
          onlineFriendsCount={onlineFriendsCount}
        />
      )}

      {/* Module Host Modal */}
      {showModuleHost && (
        <ModuleHost
          installedModules={installedModules}
          runningModules={runningModules}
          onRun={runModule}
          onStop={stopModule}
          onUninstall={uninstallModule}
          onClose={() => setShowModuleHost(false)}
        />
      )}

      {/* Module Installer Modal */}
      {showModuleInstaller && (
        <ModuleInstaller
          onLoadModule={loadModule}
          onClose={() => setShowModuleInstaller(false)}
        />
      )}

      {/* Permission Approval Modal */}
      {pendingModule && (
        <PermissionModal
          module={pendingModule.module}
          permissionCheck={pendingModule.permissionCheck}
          onApprove={approvePending}
          onDeny={denyPending}
        />
      )}

      {/* Clipboard Panel */}
      {showClipboard && (
        <div className="modal-overlay" onClick={() => setShowClipboard(false)}>
          <div className="clipboard-modal" onClick={e => e.stopPropagation()}>
            <ClipboardPanel
              clipboard={clipboard}
              onClose={() => setShowClipboard(false)}
            />
          </div>
        </div>
      )}

      {/* Account Settings Modal */}
      {showAccountSettings && (
        <div className="modal-overlay" onClick={() => setShowAccountSettings(false)}>
          <div className="account-settings-modal" onClick={e => e.stopPropagation()}>
            <AccountSettings
              identity={identity}
              onImportIdentity={importIdentity}
              onClose={() => setShowAccountSettings(false)}
            />
          </div>
        </div>
      )}

      {/* Chat Panel Modal */}
      {showChat && (
        <div className="modal-overlay" onClick={() => setShowChat(false)}>
          <div className="chat-modal" onClick={e => e.stopPropagation()}>
            <ChatPanel
              conversations={conversations}
              getConversation={getConversation}
              sendMessage={sendMessage}
              markConversationRead={markConversationRead}
              getDisplayName={getDisplayName}
              getAvatarUrl={(pubkey) => getProfileByPubkey(pubkey)?.content?.avatarDataUrl}
              isOnline={isOnline}
              friends={friends}
              onClose={() => setShowChat(false)}
              clipboardEntries={clipboard.entries}
              getAttachmentPreview={async (hash) => {
                const result = await clipboard.yank(hash);
                if (result.data && result.entry) {
                  const type = result.entry.content.content.type;
                  if (type.startsWith('image/')) {
                    const blob = new Blob([new Uint8Array(result.data)], { type });
                    return URL.createObjectURL(blob);
                  }
                }
                return null;
              }}
              onStartCall={calls.startCall}
            />
          </div>
        </div>
      )}

      {/* User Directory Modal */}
      {showUserDirectory && (
        <div className="modal-overlay" onClick={() => setShowUserDirectory(false)}>
          <div className="user-directory-modal" onClick={e => e.stopPropagation()}>
            <UserDirectory
              users={allKnownUsers}
              currentUserPubkey={identity?.publicKey}
              onAddFriend={(pubkey) => toggleFriend(pubkey)}
              onRemoveFriend={(pubkey) => removeFriend(pubkey)}
              onBlockUser={(pubkey) => toggleBlock(pubkey)}
              onViewProfile={(pubkey) => {
                setViewingProfilePubkey(pubkey);
                setShowUserDirectory(false);
              }}
              onStartChat={() => {
                setShowUserDirectory(false);
                setShowChat(true);
              }}
              onClose={() => setShowUserDirectory(false)}
            />
          </div>
        </div>
      )}

      {/* Call Panel (fullscreen overlay) */}
      <CallPanel
        activeCall={calls.activeCall}
        incomingCall={calls.incomingCall}
        onAccept={calls.acceptCall}
        onDecline={calls.declineCall}
        onEndCall={calls.endCall}
        onToggleVideo={calls.toggleVideo}
        onToggleAudio={calls.toggleAudio}
        setLocalVideoRef={calls.setLocalVideoRef}
        setRemoteVideoRef={calls.setRemoteVideoRef}
        getDisplayName={getDisplayName}
        getAvatarUrl={(pubkey) => getProfileByPubkey(pubkey)?.content?.avatarDataUrl}
      />
    </div>
  );
}

export default App;
